import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { useVerifyCheckout } from "@moonscape/shared";
import { Spinner } from "@/components/ui/spinner";
import { Screen } from "@/components/Screen";
import { Callout, DHead, Fig, Foot, Lab, Pill, Tag } from "@/components/ms";
import { trackEvent } from "@/analytics";
import { CONVERSION_EVENTS } from "@/analytics/events";

export const StripeSuccess = () => {
  const location = useLocation();
  const values = queryString.parse(location?.search);
  const sessionId = values.session_id;
  const { isQueryloading, data } = useVerifyCheckout({ sessionId });
  const trackedRef = useRef(false);

  useEffect(() => {
    if (isQueryloading || !data || trackedRef.current) {
      return;
    }

    trackEvent(CONVERSION_EVENTS.SUBSCRIPTION_PAID, {
      session_id: sessionId,
      plan_name: data?.planName || data?.plan_name,
      amount: data?.amount,
      currency: data?.currency,
      subscription_id: data?.subscriptionId || data?.subscription_id,
      source: "web",
    });

    trackedRef.current = true;
  }, [data, isQueryloading, sessionId]);

  if (isQueryloading) {
    return (
      <Screen heading="Payment received">
        <div className="flex min-h-[220px] items-center justify-center">
          <Spinner size={40} />
        </div>
      </Screen>
    );
  }

  const planName = data?.planName || data?.plan_name;

  return (
    <Screen heading="Payment received">
      <div className="mx-auto grid w-full max-w-[640px] gap-[var(--gap)]">
        <div>
          <Lab>Order · complete</Lab>
          <h1 className="mt-2.5 text-[30px] font-semibold leading-[1.1] tracking-[-0.035em] ms700:text-[36px]">
            You're upgraded.
          </h1>
          <p className="mt-2.5 max-w-[52em] text-[13.5px] leading-[1.55] text-ms-mute">
            Your figures were already calculated. Paying unlocked the pack you file and
            the workings behind it.
          </p>
        </div>

        <section>
          <DHead label="Your order" right="Paid" />
          <div className="mt-1">
            {planName && <Fig label="Plan" value={planName} />}
            <Fig label="Access" sub="Applied to your account" value="Active" tone="green" />
            <Fig label="Receipt" sub="Sent to your email" value="Sent" />
          </div>
          <div className="mt-3.5">
            <Tag tone="pos">Access activated</Tag>
          </div>
        </section>

        <Callout>
          <b>Kept current.</b> Your sources keep syncing. If a late transaction changes
          your number we recalculate and reissue the pack, free.
        </Callout>

        <div className="flex flex-col gap-2.5 ms700:flex-row">
          <Pill as="a" href="/tax">
            Go to Tax screen
          </Pill>
          <Pill as="a" variant="ghost" href="/">
            Back to dashboard
          </Pill>
        </div>
      </div>

      <Foot className="mt-[var(--gap)] px-0" right="Receipt sent">
        Every figure resolves to a source record you can export.
      </Foot>
    </Screen>
  );
};

export const StripeCancel = () => {
  return (
    <Screen heading="Checkout canceled">
      <div className="mx-auto grid w-full max-w-[640px] gap-[var(--gap)]">
        <div>
          <Lab>Order · not placed</Lab>
          <h1 className="mt-2.5 text-[30px] font-semibold leading-[1.1] tracking-[-0.035em] ms700:text-[36px]">
            Nothing was charged.
          </h1>
          <p className="mt-2.5 max-w-[52em] text-[13.5px] leading-[1.55] text-ms-mute">
            You left checkout before paying, so nothing changed on your account. Your
            figures are still calculated and still visible.
          </p>
        </div>

        <section>
          <DHead label="What changed" right="Nothing" />
          <div className="mt-1">
            <Fig label="Your card" sub="No payment was taken" value="Untouched" tone="dim" />
            <Fig label="Your figures" sub="Still calculated" value="Visible" />
            <Fig label="Your sources" sub="Still syncing" value="Connected" />
          </div>
        </section>

        <Callout>
          <b>Need a hand?</b> If something in checkout did not work, tell us what you saw
          at <a href="mailto:hello@moonscape.app">hello@moonscape.app</a> and we'll trace
          it.
        </Callout>

        <div className="flex flex-col gap-2.5 ms700:flex-row">
          <Pill as="a" href="/plans">
            Try again
          </Pill>
          <Pill as="a" variant="ghost" href="/">
            Back to dashboard
          </Pill>
        </div>
      </div>

      <Foot className="mt-[var(--gap)] px-0" right="One-off · not a subscription">
        No charge was made. Nothing recurring, ever.
      </Foot>
    </Screen>
  );
};
